import walk from 'model/actions/walk';
import depart from 'model/actions/depart';
import hunt from 'model/actions/hunt';
import rest from 'model/actions/rest';
import forage, { ForageType } from 'model/actions/forage';
import sleep from 'model/actions/city/sleep';
import beg from 'model/actions/city/beg';
import examineMap from 'model/actions/city/examineMap';
import rob from 'model/actions/city/rob';
import work from 'model/actions/city/work';
import buyMedicine from 'model/actions/city/buyMedicine';
import tradePost, { ActionType as TradePostType } from 'model/actions/city/tradePost';

/**
 * Modernization:
 * - DrinkPuddle
 * - Walk
 * - Sleep
 * - Beg
 * - TakeBowl
 * - Work
 */
const isActionVisible = (phase, index) => phase.actions.includes(index);
const isCityActionVisible = (phase, index) => phase.cityActions.includes(index);

const ActionTypes = {
  Walk: 'Walk',
  Hunt: 'Hunt',
  Forage: ForageType,
  Rest: 'Rest',
  Depart: 'Depart'
};

const createAction = (action, index) => ({
  ...action,
  index: ActionTypes[index],
  isVisible: isActionVisible
});

/**
 * Depart has two homes and is never in both at once.
 *
 * On the open road it is `Continue`, a travelling verb, and the menu beside Forage and Hunt is
 * where it belongs. Standing in a town it is the door out — not something you do here, but the
 * one move that changes where you are — so the town page renders it at the foot of its own
 * screen instead. Same argument that moved Examine Map into the town's column; see phases.js.
 *
 * `party.city` is the source of truth for standing in a town, not `party.phase.name` (which
 * stays 'city' forever) and not the header tab (which only says what you are looking at). It is
 * set on arrival and cleared by the first step out — party.setPoliticalContext.
 */
const departsFromTown = party => !!party.city;

const Actions = {
  walk: createAction(walk, 'Walk'),
  forage: createAction(forage, 'Forage'),
  hunt: createAction(hunt, 'Hunt'),
  rest: createAction(rest, 'Rest'),
  depart: createAction(depart, 'Depart')
};

const CityActionTypes = {
  BuyMedicine: 'BuyMedicine',
  TradePost: TradePostType,
  Beg: 'Beg',
  ExamineMap: examineMap.actionName,
  Rest: 'Rest',
  Rob: 'Rob',
  Work: 'Work'
};

const CityActions = {
  // First in the object is first in the town's left column. The map is a one-shot that opens the
  // barkeep fork, so it wants to be the thing noticed when it appears, above the four verbs that
  // repeat. It spends no day of its own — the split here is which column, not what it costs.
  examineMap: () => ({
    ...examineMap,
    index: CityActionTypes.ExamineMap,
    isActivity: true
  }),
  sleep: () => ({
    ...sleep,
    index: CityActionTypes.Rest,
    isActionVisible,
    isActivity: true
  }),
  beg: () => ({
    ...beg,
    index: CityActionTypes.Beg,
    isActionVisible,
    isActivity: true
  }),
  rob: () => ({
    ...rob,
    index: CityActionTypes.Rob,
    isActionVisible,
    isActivity: true
  }),
  work: () => ({
    ...work,
    index: CityActionTypes.Work,
    isActionVisible,
    isActivity: true
  }),
  buyMedicine: ({ context, prices }) => ({
    ...buyMedicine({ context, prices }),
    index: CityActionTypes.BuyMedicine,
    isActivity: false
  }),
  tradePost: ({ context }) => ({
    ...tradePost({ context }),
    index: CityActionTypes.TradePost,
    isActivity: false
  })
};

export { ActionTypes, CityActions, CityActionTypes, departsFromTown, isActionVisible, isCityActionVisible };
export default Actions;
